import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RideDetail } from '@ridemaadi/types';
import { useAuth } from '../../../providers/AuthProvider';
import { ridesApi } from '../api';
import { rideKeys } from '../hooks';
import { RideCompletionCelebration } from './RideCompletionCelebration';
import { wasCelebrationShown } from './celebrationUtils';

interface RideCompletionContextValue {
  showForRideId: (rideId: string) => Promise<void>;
}

const RideCompletionContext = createContext<RideCompletionContextValue | null>(null);

export function RideCompletionCelebrationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [ride, setRide] = useState<RideDetail | null>(null);
  const showingRef = useRef<string | null>(null);

  const showForRideId = useCallback(
    async (rideId: string) => {
      if (!rideId || wasCelebrationShown(rideId) || showingRef.current === rideId) return;

      let detail = qc.getQueryData<RideDetail>(rideKeys.detail(rideId));
      if (!detail || detail.status !== 'completed') {
        try {
          detail = await qc.fetchQuery({
            queryKey: rideKeys.detail(rideId),
            queryFn: () => ridesApi.get(rideId),
          });
        } catch {
          return;
        }
      }

      if (!detail || detail.status !== 'completed') return;
      if (document.visibilityState !== 'visible') return;

      showingRef.current = rideId;
      setRide(detail);
    },
    [qc],
  );

  const dismiss = useCallback(() => {
    showingRef.current = null;
    setRide(null);
  }, []);

  return (
    <RideCompletionContext.Provider value={{ showForRideId }}>
      {children}
      {ride && (
        <RideCompletionCelebration ride={ride} user={user} onDismiss={dismiss} />
      )}
    </RideCompletionContext.Provider>
  );
}

export function useRideCompletionCelebration() {
  const ctx = useContext(RideCompletionContext);
  if (!ctx) {
    throw new Error('useRideCompletionCelebration must be used within RideCompletionCelebrationProvider');
  }
  return ctx;
}
